import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpRight,
  Ban,
  Check,
  Code2,
  Copy,
  FileText,
  Info,
  Loader2,
  RefreshCw,
  ScrollText,
  XCircle,
} from 'lucide-react';
import {
  Alert,
  Badge,
  FinancialStatusBadge,
  PageHeader,
  ToastContainer,
  useToast,
} from '../ui';
import { financialsService } from '../../services/financials.service';
import organizationService from '../../services/organization.service';
import CustomerHoverCard from '../shared/CustomerHoverCard';

/* ── Helpers ──────────────────────────────────────────────────────────────── */
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtRelative = (v) => {
  if (!v) return '—';
  const diff = Math.floor((Date.now() - new Date(v)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return fmtDateTime(v);
};

// Mirrors the statuses the header's "Awaiting review" callout treats as
// still in-flight — only intents that haven't reached a terminal outcome
// yet can be cancelled. The gateway also re-validates this server-side.
const CANCELLABLE_STATUSES = new Set(['CREATED', 'PENDING_APPROVAL', 'PENDING_PAYMENT_ACTION']);

const fmtAmount = (amount) => {
  if (amount == null) return '—';
  const n = parseFloat(amount);
  if (isNaN(n)) return String(amount);
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* ── InlineCopy ───────────────────────────────────────────────────────────── */
const InlineCopy = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="ml-1 text-ink-400 hover:text-ink-700 transition-colors inline-flex items-center"
      title={copied ? 'Copied!' : 'Copy'}
    >
      {copied ? <Check className="h-3 w-3 text-success-600" /> : <Copy className="h-3 w-3" />}
    </button>
  );
};

/* ── InfoRow ──────────────────────────────────────────────────────────────── */
const InfoRow = ({ label, value, mono = false, copyable = false, children }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-ink-100 last:border-0">
    <span className="text-xs text-ink-500 shrink-0 w-40">{label}</span>
    <div className="flex items-center min-w-0 flex-1 justify-end text-right">
      {children ?? (
        <>
          <span className={`text-sm text-ink-800 break-all ${mono ? 'font-mono' : ''}`}>
            {value ?? '—'}
          </span>
          {copyable && value && <InlineCopy value={String(value)} />}
        </>
      )}
    </div>
  </div>
);

/* ── Tab bar ──────────────────────────────────────────────────────────────── */
const TABS = [
  { id: 'overview', label: 'Overview', icon: Info },
  { id: 'timeline', label: 'Timeline', icon: ScrollText },
  { id: 'raw', label: 'Raw Details', icon: Code2 },
];

const TabBar = ({ active, onChange }) => (
  <div className="flex border-b border-ink-200 mb-6">
    {TABS.map((t) => {
      const Icon = t.icon;
      const isActive = active === t.id;
      return (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px
            ${isActive
              ? 'border-accent-600 text-accent-700'
              : 'border-transparent text-ink-500 hover:text-ink-800 hover:border-ink-300'
            }`}
        >
          <Icon className="h-3.5 w-3.5" />
          {t.label}
        </button>
      );
    })}
  </div>
);

/* ── Status-specific callout ──────────────────────────────────────────────── */
const StatusCallout = ({ transaction, type }) => {
  if (!transaction) return null;

  if (transaction.status === 'REJECTED') {
    // The backend records the rejection reason in the intent's general
    // `notes` field (set by the reviewing admin) rather than a dedicated
    // field — there is no separate rejectionReason on the wire.
    return (
      <div className="col-span-2">
        <div className="rounded-lg border border-danger-200 bg-danger-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <XCircle className="h-4 w-4 text-danger-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-danger-800">Transaction rejected</p>
              {transaction.notes && (
                <p className="text-sm text-danger-700 mt-0.5">{transaction.notes}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (transaction.status === 'PENDING_APPROVAL' || transaction.status === 'CREATED') {
    return (
      <div className="col-span-2">
        <div className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <Info className="h-4 w-4 text-warning-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-warning-800">Awaiting review</p>
              <p className="text-sm text-warning-700 mt-0.5">
                {type === 'DEPOSIT'
                  ? 'This deposit is pending manual review by the compliance team.'
                  : 'This withdrawal request is pending manual approval before processing.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (type === 'DEPOSIT' && transaction.autoApproved === true) {
    return (
      <div className="col-span-2">
        <div className="rounded-lg border border-success-200 bg-success-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <Check className="h-4 w-4 text-success-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-success-800">Auto-approved</p>
              <p className="text-sm text-success-700 mt-0.5">
                This deposit was automatically approved and applied to the customer account.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

/* ── OVERVIEW TAB ─────────────────────────────────────────────────────────── */
const OverviewTab = ({ transaction, type }) => {
  if (!transaction) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Status callout */}
      <StatusCallout transaction={transaction} type={type} />

      {/* Transaction details */}
      <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-ink-100">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Transaction details
          </p>
        </div>
        <div className="px-5">
          <InfoRow label="Type" value={type === 'DEPOSIT' ? 'Deposit' : 'Withdrawal'} />
          <InfoRow
            label="Amount"
            value={`${fmtAmount(transaction.amount)} ${transaction.currency || ''}`.trim()}
            mono
          />
          <InfoRow label="Currency" value={transaction.currency} />
          <InfoRow label="Status">
            <FinancialStatusBadge status={transaction.status} />
          </InfoRow>
          <InfoRow label="Created" value={fmtDateTime(transaction.createdAt)} />
          <InfoRow label="Last updated" value={fmtDateTime(transaction.updatedAt)} />
          {transaction.transferMethod && (
            <InfoRow label="Transfer method" value={transaction.transferMethod} />
          )}
          {type === 'DEPOSIT' && transaction.autoApproved != null && (
            <InfoRow label="Auto-approved" value={transaction.autoApproved ? 'Yes' : 'No'} />
          )}
          {transaction.notes && (
            <InfoRow label="Notes" value={transaction.notes} />
          )}
        </div>
      </div>

      {/* Reference */}
      <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-ink-100">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            References
          </p>
        </div>
        <div className="px-5">
          <InfoRow
            label="Reference code"
            value={transaction.referenceCode}
            mono
            copyable
          />
          <InfoRow
            label="Intent ID"
            value={transaction.intentId || transaction.id}
            mono
            copyable
          />
          {transaction.bankAccountId && (
            <InfoRow
              label="Bank account ID"
              value={transaction.bankAccountId}
              mono
              copyable
            />
          )}
        </div>
      </div>

      {/* Customer */}
      <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
        <div className="px-5 py-4 border-b border-ink-100">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            Customer
          </p>
        </div>
        <div className="px-5">
          <InfoRow label="Customer ID">
            <div className="flex items-center gap-2">
              <CustomerHoverCard customerId={transaction.customerId}>
                <span className="text-sm font-mono text-ink-800 break-all">
                  {transaction.customerId || '—'}
                </span>
              </CustomerHoverCard>
              {transaction.customerId && (
                <InlineCopy value={transaction.customerId} />
              )}
            </div>
          </InfoRow>
        </div>
      </div>

      {/* Bank account details (withdrawals) */}
      {type === 'WITHDRAWAL' && transaction.userBankAccount && (
        <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-ink-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
              Bank account
            </p>
          </div>
          <div className="px-5">
            <InfoRow
              label="Beneficiary"
              value={transaction.userBankAccount.beneficiaryName}
            />
            <InfoRow
              label="Bank"
              value={transaction.userBankAccount.bankName}
            />
            <InfoRow
              label="IBAN"
              value={transaction.userBankAccount.iban}
              mono
              copyable
            />
            <InfoRow
              label="SWIFT"
              value={transaction.userBankAccount.swiftCode}
              mono
            />
            <InfoRow
              label="Currency"
              value={transaction.userBankAccount.currency}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* ── TIMELINE TAB ─────────────────────────────────────────────────────────── */
const auditOutcomeTone = (o) =>
  o === 'SUCCESS' ? 'success' : o === 'FAILURE' ? 'danger' : o === 'REJECTED' ? 'warning' : 'neutral';

const TimelineTab = ({ intentId }) => {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    organizationService
      .listAuditEvents({ limit: 50, resourceId: intentId })
      .then((data) => {
        const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
        setEvents(items);
      })
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load audit history.'))
      .finally(() => setLoading(false));
  }, [intentId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-ink-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading timeline…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert tone="danger" action={
        <button type="button" onClick={load} className="btn btn-sm btn-secondary">Retry</button>
      }>
        {error}
      </Alert>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-12 w-12 rounded-lg bg-ink-100 flex items-center justify-center mb-4">
          <ScrollText className="h-6 w-6 text-ink-400" />
        </div>
        <h3 className="text-sm font-semibold text-ink-900">No timeline events</h3>
        <p className="mt-1 text-sm text-ink-500 max-w-sm">
          No audit events have been recorded for this transaction yet.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-5 top-0 bottom-0 w-px bg-ink-200" />
      <ul className="space-y-0">
        {events.map((event, i) => (
          <li key={event.id || i} className="relative flex gap-5 pb-6">
            {/* Dot */}
            <div className={`relative z-10 h-10 w-10 rounded-full flex items-center justify-center shrink-0 border-2 border-white
              ${event.outcome === 'SUCCESS' ? 'bg-success-100'
                : event.outcome === 'FAILURE' ? 'bg-danger-100'
                : event.outcome === 'REJECTED' ? 'bg-warning-100'
                : 'bg-ink-100'
              }`}
            >
              <FileText className={`h-4 w-4
                ${event.outcome === 'SUCCESS' ? 'text-success-600'
                  : event.outcome === 'FAILURE' ? 'text-danger-600'
                  : event.outcome === 'REJECTED' ? 'text-warning-600'
                  : 'text-ink-500'
                }`}
              />
            </div>
            {/* Content */}
            <div className="flex-1 min-w-0 pt-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge tone={auditOutcomeTone(event.outcome)} dot>
                  {event.action || event.eventType || 'event'}
                </Badge>
                <time
                  className="text-xs text-ink-400 tnum"
                  title={fmtDateTime(event.createdAt || event.timestamp)}
                >
                  {fmtRelative(event.createdAt || event.timestamp)}
                </time>
              </div>
              {event.actorId && (
                <p className="text-xs text-ink-500 mt-1 font-mono truncate">
                  Actor: {event.actorId}
                </p>
              )}
              {event.description && (
                <p className="text-xs text-ink-600 mt-1">{event.description}</p>
              )}
              {event.reasonCode && (
                <p className="text-xs text-ink-500 mt-0.5 font-mono">
                  Code: {event.reasonCode}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ── RAW DETAILS TAB ──────────────────────────────────────────────────────── */
const RawDetailsTab = ({ transaction }) => {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(transaction, null, 2);

  const copy = () => {
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Raw JSON</p>
        <button
          type="button"
          onClick={copy}
          className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors
            ${copied
              ? 'bg-success-50 border-success-200 text-success-700'
              : 'bg-ink-50 border-ink-200 text-ink-600 hover:bg-ink-100'
            }`}
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy JSON'}
        </button>
      </div>
      <pre className="bg-ink-950 text-ink-100 rounded-xl p-5 text-xs font-mono leading-relaxed overflow-x-auto max-h-[600px] overflow-y-auto">
        {json}
      </pre>
    </div>
  );
};

/* ── Main component ───────────────────────────────────────────────────────── */
const FinancialDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toasts, show, dismiss } = useToast();

  const type = (searchParams.get('type') || 'DEPOSIT').toUpperCase();

  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const loadTransaction = useCallback(() => {
    setLoading(true);
    setError(null);
    const fetcher = type === 'WITHDRAWAL'
      ? financialsService.getWithdrawal(id)
      : financialsService.getDeposit(id);

    fetcher
      .then((data) => setTransaction(data))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load transaction.'))
      .finally(() => setLoading(false));
  }, [id, type]);

  useEffect(() => { loadTransaction(); }, [loadTransaction]);

  const handleCopyId = () => {
    navigator.clipboard.writeText(id).then(() => show('Transaction ID copied', 'success'));
  };

  const isDeposit = type === 'DEPOSIT';
  const intentId = transaction?.intentId || transaction?.id || id;
  const canCancel = transaction && CANCELLABLE_STATUSES.has(transaction.status);

  const handleCancel = async () => {
    if (!window.confirm(`Cancel this ${isDeposit ? 'deposit' : 'withdrawal'}? This cannot be undone.`)) return;
    setCancelError(null);
    setCancelling(true);
    try {
      if (isDeposit) {
        await financialsService.cancelDeposit(intentId);
      } else {
        await financialsService.cancelWithdrawal(intentId);
      }
      show(`${isDeposit ? 'Deposit' : 'Withdrawal'} cancelled`, 'success');
      loadTransaction();
    } catch (err) {
      setCancelError(err?.response?.data?.message || 'Failed to cancel transaction.');
    } finally {
      setCancelling(false);
    }
  };

  const headerTitle = loading
    ? (isDeposit ? 'Deposit' : 'Withdrawal')
    : transaction
    ? `${isDeposit ? 'Deposit' : 'Withdrawal'} — ${transaction.referenceCode || id}`
    : (isDeposit ? 'Deposit' : 'Withdrawal');

  return (
    <div>
      <ToastContainer toasts={toasts} onDismiss={dismiss} />

      <PageHeader
        title={headerTitle}
        breadcrumb={['Cash In/Out', isDeposit ? 'Deposit' : 'Withdrawal', transaction?.referenceCode || id]}
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/financials')}
              className="btn btn-sm btn-secondary"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All transactions
            </button>
            <button
              type="button"
              onClick={handleCopyId}
              className="btn btn-sm btn-secondary"
            >
              <Copy className="h-3.5 w-3.5" /> Copy ID
            </button>
            <button
              type="button"
              onClick={loadTransaction}
              className="btn btn-sm btn-secondary"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            {canCancel && (
              <button
                type="button"
                onClick={handleCancel}
                disabled={cancelling}
                className="btn btn-sm btn-danger disabled:opacity-50"
              >
                {cancelling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
                Cancel {isDeposit ? 'deposit' : 'withdrawal'}
              </button>
            )}
          </div>
        }
      />

      {cancelError && (
        <div className="mb-4">
          <Alert tone="danger">{cancelError}</Alert>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-ink-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading transaction…</span>
        </div>
      ) : error ? (
        <Alert
          tone="danger"
          action={
            <button type="button" onClick={loadTransaction} className="btn btn-sm btn-secondary">
              Retry
            </button>
          }
        >
          {error}
        </Alert>
      ) : transaction ? (
        <>
          {/* Header card */}
          <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden mb-6">
            <div className="px-6 py-5 flex items-center gap-5 flex-wrap">
              {/* Type icon */}
              <div
                className={`h-14 w-14 rounded-full flex items-center justify-center shrink-0 ${
                  isDeposit ? 'bg-success-100' : 'bg-danger-100'
                }`}
              >
                {isDeposit ? (
                  <ArrowDownLeft className="h-7 w-7 text-success-700" />
                ) : (
                  <ArrowUpRight className="h-7 w-7 text-danger-700" />
                )}
              </div>

              {/* Main info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h2
                    className={`text-2xl font-bold font-mono tnum ${
                      isDeposit ? 'text-success-700' : 'text-danger-700'
                    }`}
                  >
                    {isDeposit ? '+' : '−'}{fmtAmount(transaction.amount)}
                    <span className="ml-2 text-base font-normal text-ink-400">
                      {transaction.currency}
                    </span>
                  </h2>
                  <FinancialStatusBadge status={transaction.status} />
                </div>
                <div className="mt-1 flex items-center gap-4 flex-wrap">
                  {transaction.referenceCode && (
                    <span className="text-sm font-mono text-ink-500">
                      {transaction.referenceCode}
                    </span>
                  )}
                  <span className="text-sm text-ink-500">
                    {isDeposit ? 'Deposit' : 'Withdrawal'}
                  </span>
                </div>
              </div>

              {/* Meta pills */}
              <div className="flex items-center gap-2 flex-wrap shrink-0">
                {transaction.referenceCode && (
                  <div className="flex items-center gap-1 rounded-lg bg-ink-50 border border-ink-200 px-3 py-1.5">
                    <span className="text-2xs text-ink-400 uppercase tracking-wider mr-1">REF</span>
                    <span className="text-xs font-mono text-ink-700">{transaction.referenceCode}</span>
                    <InlineCopy value={transaction.referenceCode} />
                  </div>
                )}
                <div className="flex items-center gap-1 rounded-lg bg-ink-50 border border-ink-200 px-3 py-1.5">
                  <span className="text-2xs text-ink-400 uppercase tracking-wider mr-1">ID</span>
                  <span className="text-xs font-mono text-ink-700 max-w-[120px] truncate">
                    {intentId}
                  </span>
                  <InlineCopy value={intentId} />
                </div>
              </div>
            </div>

            {/* Quick-stat bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 border-t border-ink-100 divide-x divide-ink-100">
              {[
                { label: 'Customer', value: transaction.customerId, mono: true, copyable: true },
                { label: 'Amount', value: `${fmtAmount(transaction.amount)} ${transaction.currency || ''}`.trim(), mono: true },
                { label: 'Created', value: fmtDateTime(transaction.createdAt) },
                { label: 'Updated', value: fmtDateTime(transaction.updatedAt) },
              ].map((item) => (
                <div key={item.label} className="px-5 py-3">
                  <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400 mb-1">
                    {item.label}
                  </p>
                  <div className="flex items-center gap-1">
                    <span className={`text-xs text-ink-700 truncate ${item.mono ? 'font-mono' : ''}`}>
                      {item.value || '—'}
                    </span>
                    {item.copyable && item.value && item.value !== '—' && (
                      <InlineCopy value={item.value} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <TabBar active={activeTab} onChange={setActiveTab} />

          {activeTab === 'overview' && (
            <OverviewTab transaction={transaction} type={type} />
          )}
          {activeTab === 'timeline' && (
            <TimelineTab intentId={intentId} />
          )}
          {activeTab === 'raw' && (
            <RawDetailsTab transaction={transaction} />
          )}
        </>
      ) : null}
    </div>
  );
};

export default FinancialDetailPage;
